<template>
    <div>

        <div class="columns" v-if="canManageUsers">

            <div class="column is-half">
                <b-field>
                    <b-checkbox v-model="options.archived">
                        Архивированные пользователи
                    </b-checkbox>
                </b-field>
            </div>
            
            <div class="column has-text-weight-bold has-text-right">
                <span v-show="total > 0">
                    Всего: {{ total }}
                </span>
            </div>
        
        </div>

        <b-field v-if="canManageUsers && isStudent">
                <b-checkbox v-model="options.foreign">
                    Иностранцы
                </b-checkbox>
        </b-field>

        <div class="block" v-if="canViewStatistics">
            <b-radio
                v-model="options.verified"
                :native-value="null">
                <span>Все пользователи</span>
            </b-radio>

            <b-radio
                v-model="options.verified"
                native-value="true">
                <span>Подтверждённые</span>
            </b-radio>

            <b-radio
                v-model="options.verified"
                native-value="false">
                <span>Неподтверждённые</span>
            </b-radio>
        </div>

        <div class="columns">

            <div class="column">

                <div class="is-hidden-tablet">

                    <b-field>
                        <type-select
                            expanded
                            v-if="type == '*'"
                            :options="options"
                            @input="onTypeChange"
                        />
                    </b-field>

                    <b-field>
                        <name-field
                            expanded
                            :options="options"
                            @clear="clear"
                        />
                    </b-field>

                    <b-field></b-field>

                </div>

                <b-field class="is-hidden-mobile">
                    <p class="control" v-if="type == '*'">
                        <type-select
                            :options="options"
                            @input="onTypeChange"    
                        />
                    </p>
                    <name-field
                        expanded
                        :options="options"
                        @clear="clear"
                    />
                </b-field>

            </div>

            <div class="column is-one-third" v-if="!minimal && isStudent">
                <b-field>

                    <b-field>

                        <b-radio-button
                            @input="search"
                            class="is-expanded is-fullwidth"
                            v-model="options.profile.enrollment_year"
                            :native-value="null">
                            <span>Любой курс</span>
                        </b-radio-button>

                        <b-radio-button
                            @input="search"
                            class="is-expanded is-fullwidth"
                            v-for="course in courses"
                            :key="course.number"
                            v-model="options.profile.enrollment_year"
                            :native-value="course.enrollment_year"
                            :disabled="!course.exists">
                            <span>{{ course.number }}</span>
                        </b-radio-button>

                    </b-field>
                    
                </b-field>
            </div>

            <div class="column is-one-third" v-else-if="!minimal && isEmployee">
                <b-field>
                    <b-select
                        expanded
                        placeholder="Отдел или кафедра"
                        @input="search"
                        v-model="options.profile.department">
                        <option :value="null">Любой отдел / кафедра</option>
                        <option
                            v-for="department in filteredDepartments"
                            :value="department.id"
                            :key="department.id"
                            v-text="department.name"
                        />
                    </b-select>
                </b-field>
            </div>

        </div>
        
        <table-skeleton v-if="initialLoad"></table-skeleton>

        <b-table ref="table"
            @click="openUserProfile"
            @sort="sort"
            v-show="!initialLoad"
            :data="items"
            :row-class="(row, index) => minimal ? `` : `interactive`"
            v-infinite-scroll="debouncedFetch"
            infinite-scroll-disabled="finishedLoading"
            infinite-scroll-distance="750"
            striped
            backend-sorting
            
            :hoverable="minimal"
            :checkable="minimal"
            :is-row-checkable="getCheckable"
            :checked-rows.sync="checked"
            :custom-is-checked="(a, b) => { return a.id === b.id }"
            @check="check">

            <template slot-scope="props">

                <b-table-column
                    field="last_name"
                    label="Фамилия"
                    sortable>
                    {{ props.row.last_name }}
                </b-table-column>

                <b-table-column
                    field="first_name"
                    label="Имя"
                    sortable>
                    {{ props.row.first_name }}
                </b-table-column>

                <b-table-column
                    field="middle_name"
                    label="Отчество"
                    sortable>
                    {{ props.row.middle_name }}
                </b-table-column>

                <b-table-column
                    :visible="isEmployee && isTableReady"
                    sortable
                    field="position"
                    :label="positionLabel(props.row)">
                    {{ positionName(props.row) }}
                </b-table-column>

                <b-table-column
                    :visible="isEmployee && isTableReady"
                    sortable
                    field="department"
                    :label="departmentLabel(props.row)">
                    {{ departmentName(props.row) }}
                </b-table-column>

                <b-table-column
                    :visible="isStudent && isTableReady && ! minimal"
                    sortable
                    field="student_type"
                    label="Вид подготовки">
                    {{ props.row.type == 'student' ? $t('students.type.' + props.row.profile.type) : '' }}
                </b-table-column>

                <b-table-column
                    :visible="isAny && isTableReady"
                    field="user_type"
                    label="Статус">
                    {{ $t('users.profile.type.' + props.row.profile.type) }}
                </b-table-column>

                <b-table-column
                    centered
                    :visible="isStudent && isTableReady && ! minimal && ! options.archived"
                    sortable
                    field="course"
                    label="Курс">
                    {{ props.row.profile.course }}
                </b-table-column>

                <b-table-column
                    centered
                    :visible="isStudent && isTableReady && ! minimal && options.archived"
                    sortable
                    field="enrollment_year"
                    label="Год начала обучения">
                    {{ props.row.profile.enrollment_year }}
                </b-table-column>

                <b-table-column
                    centered
                    :visible="isStudent && !isGraduate && isTableReady && (! minimal || groupView) && ! options.archived"
                    sortable
                    field="group"
                    label="Группа">
                    <template v-if="groupView">
                        <template v-if="getGroupConflict(props.row)">
                        <b-tooltip
                            animated
                            multilined
                            type="is-warning"
                            position="is-left"
                            label="Студент уже находится в другой группе данного типа.
                            Добавление его в эту группу исключит его из его текущей группы.">
                            <span class="has-text-warning">
                                {{ getGroupTitle(props.row) }}
                            </span>
                        </b-tooltip>
                        </template>
                        <template v-else>
                            {{ getGroupTitle(props.row) }}
                        </template>
                    </template>
                    <template v-else>
                        {{ $_.get(props.row.profile, 'regular_group.title', "-") }}
                    </template>
                </b-table-column>

                <b-table-column
                    :visible="! minimal && options.archived"
                    field="comment"
                    label="Комментарий">
                    {{ commentLabel(props.row) }}
                </b-table-column>

                <b-table-column
                    :visible="! minimal && canViewStatistics"
                    sortable
                    centered
                    field="verified"
                    label="Подтвержден">
                    <b-icon :icon="props.row.verified ?
                    'check-circle-outline' :
                    'checkbox-blank-circle-outline'"
                    :type="props.row.verified ?
                    'is-success' :
                    ''"
                    size="is-small"></b-icon>
                </b-table-column>

            </template>

            <div slot="empty" class="has-text-centered" v-show="!initialLoad">
                Нет результатов
            </div>

        </b-table>

        <b-loading :active="loading"></b-loading>
    </div>
</template>

<script>
import RepositorySearch from "../../abstract/RepositorySearch.js";

import TypeSelect from "./index/components/TypeSelect.vue";
import NameField from "./index/components/NameField.vue";

export default RepositorySearch.extend({
  components: {
    TypeSelect,
    NameField
  },
  props: {
    type: {
      type: [String],
      default: "*"
    },
    groupFilter: {
      type: Object
    },
    profileOptions: {
      type: [Object],
      default: () => {
        return {};
      }
    },
    isMinimal: {
      type: Boolean,
      default: false
    },
    group: {
      type: Object
    },
    groupView: {
      type: Boolean,
      default: false
    },
    departments: Array,
    currentAcademicYear: {
      required: true
    },
    canViewStatistics: Boolean,
    canManageUsers: Boolean
  },
  created() {
    this.options.type = this.type;
    this.options.profile = this.profileOptions;

    if (!this.options.profile.enrollment_year) {
      this.options.profile.enrollment_year = null;
    }
  },
  data() {
    return {
      options: {
        type: "*",
        name: "",
        archived: false,
        verified: null,
        profile: {}
      },
      sortColumn: "last_name",
      endpoint: "/api/users",
      limit: 20,
      syncOrder: {
        first_name: {
          reverse: false,
          fields: ["first_name", "last_name", "middle_name"]
        },
        last_name: {
          reverse: false,
          fields: ["first_name", "last_name", "middle_name"]
        },
        middle_name: {
          reverse: false,
          fields: ["first_name", "last_name", "middle_name"]
        }
      },
      checked: []
    };
  },
  computed: {
    minimal() {
      return this.isMinimal || this.groupView;
    },
    groupType() {
      let type = _.get(this.group, "type");

      if (
        type == "linguistic" &&
        _.has(this.group, "details.subject.details.linguistic")
      ) {
        if (
          _.get(this.group, "details.subject.details.linguistic") == "primary"
        ) {
          type = "primary_language";
        } else {
          type = "secondary_language";
        }
      }

      return type;
    },
    profileOptionsEmpty: function() {
      return _.keys(this.profileOptions).length == 0;
    },
    isTeacher: function() {
      return this.options.type == "employee.teacher";
    },
    isAssociate: function() {
      return this.options.type == "employee.associate";
    },
    isAnyEmployee: function() {
      return this.options.type == "employee.*";
    },
    isEmployee: function() {
      return this.isTeacher || this.isAssociate || this.isAnyEmployee;
    },
    isBachelor: function() {
      return this.options.type == "student.bachelor";
    },
    isMaster: function() {
      return this.options.type == "student.master";
    },
    isGraduate: function() {
      return this.options.type == "student.graduate";
    },
    isAnyStudent: function() {
      return this.options.type == "student.*";
    },
    isStudent: function() {
      return (
        this.isBachelor || this.isMaster || this.isGraduate || this.isAnyStudent
      );
    },
    isAny: function() {
      return this.options.type == "*";
    },
    filteredDepartments() {
      let departments = this.departments;

      if (this.isAssociate) {
        return _.filter(departments, { academic: false });
      } else if (this.isStudent || this.isTeacher) {
        return _.filter(departments, { academic: true });
      }

      return departments;
    },
    departmentSelectLabel() {
      if (this.isAssociate) {
        return "Отдел";
      } else if (this.isStudent || this.isTeacher) {
        return "Кафедра";
      }

      return "Отдел / Кафедра";
    },
    courses() {
      let courses = [];

      for (let course = 1; course <= 4; course++) {
        let exists =
          this.isAnyStudent ||
          this.isBachelor ||
          (this.isMaster && course <= 2) ||
          (this.isGraduate && course <= 3);

        courses.push({
          number: course,
          exists: exists,
          enrollment_year: String(
            Number(this.currentAcademicYear) - (course - 1)
          )
        });
      }

      return courses;
    }
  },
  methods: {
    mergeOptions: function(options) {
      return _.merge(
        {
          group_filter: this.groupFilter || {},
          limit: this.limit
        },
        options
      );
    },
    getCheckable(user) {
      if (this.groupView) {
        if (this.groupType == "optional") {
          let groups = _.get(user, `profile.${this.groupType}_groups`);
          
          for (let group of groups) {
            if (group.id == this.group.id) {
              return false;
            }
          }

          return true;

        } else {
          let groupId = _.get(user, `profile.${this.groupType}_group.id`);
          return _.get(this.group, "id") != groupId;
        }
      } else {
        return this.minimal;
      }
    },
    getGroupConflict(user) {
      let groupId = _.get(user, `profile.${this.groupType}_group.id`);
      return groupId && _.get(this.group, "id") != groupId;
    },
    getGroupTitle(user) {
      if (this.groupView) {
        if (this.groupType == "optional") {
          let groups = _.get(user, `profile.${this.groupType}_groups`);

          for (let group of groups) {
            if (group.id == this.group.id) {
              return "Уже в этой группе";
            }
          }
        } else if (this.getGroupConflict(user) == false) {
          return "Уже в этой группе";
        }
      }

      return _.get(user, `profile.${this.groupType}_group.title`, "-");
    },
    clear: function() {
      this.options.name = "";
    },
    openUserProfile: function(user) {
      if (!this.minimal) {
        window.open(user.url, "_self");
      }
    },
    positionLabel: function(user) {
      return "Должность";
    },
    positionName: function(user) {
      if (user.profile && user.profile.position) {
        return user.profile.position.name;
      }
      return "";
    },
    departmentLabel: function(user) {
      if (this.isTeacher) {
        return "Кафедра";
      } else if (this.isAssociate) {
        return "Отдел";
      } else {
        return "Отдел / Кафедра";
      }
    },
    departmentName: function(user) {
      if (user.profile && user.profile.department) {
        return user.profile.department.name;
      }
      return "";
    },
    commentLabel: function(user) {
      switch (user.type) {
        case "employee": {
          return "Уволен";
        }
        case "student": {
          if (user.profile.graduated) {
            return `Выпускник ${user.profile.graduation_year}`;
          } else if (user.archived) {
            return "Отчислен";
          }
        }
      }
    },
    nameParameterFilter: function(value) {
      value = _.trim(value);
      return value.length >= 1 ? value : null;
    },
    check: function(checked) {
      this.$emit("check", checked);
    },
    onTypeChange() {
      if (!this.initialLoad) {
        this.options.profile.department = null;
        this.options.profile.enrollment_year = null;
        this.sortColumn = "last_name";
        this.options.order = {
          last_name: this.sortColumnDirection
        };
      }
      this.resetTable();
    }
  }
});
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/views/users/Index.vue